import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const JobManager = ({ isLightMode }) => {
  const [jobs, setJobs] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
      title: "", department: "", location: "Borehamwood, UK", type: "Full-time", description: ""
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("trade_token");
      const response = await axios.get(`${BACKEND_URL}/api/admin/jobs`, {
          headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
      if(!window.confirm("Are you sure?")) return;
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("trade_token");
      try {
          await axios.delete(`${BACKEND_URL}/api/jobs/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Job Deleted");
          fetchJobs();
      } catch (error) {
          toast.error("Delete Failed");
      }
  };

  const handleSave = async () => {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("trade_token");
      try {
          await axios.post(`${BACKEND_URL}/api/jobs`, formData, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Job Posted");
          setIsDialogOpen(false);
          setFormData({ title: "", department: "", location: "Borehamwood, UK", type: "Full-time", description: "" });
          fetchJobs();
      } catch (error) {
          toast.error("Failed to post job");
      } finally {
          setLoading(false);
      }
  };

  const inputClass = `rounded-none ${isLightMode ? 'bg-gray-50 border-gray-300 text-gray-900' : 'bg-black/50 border-white/10 text-white'}`;
  const labelClass = `text-xs uppercase font-bold mb-2 block ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className={`rounded-sm border ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`}>
      <div className="p-6 border-b border-white/10 flex justify-between items-center">
        <div>
            <h2 className={`text-xl font-bold uppercase tracking-widest ${isLightMode ? 'text-gray-900' : 'text-white'}`}>Recruitment</h2>
            <p className={`text-sm ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`}>Manage open positions on the Careers page.</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-dreibach-teal text-white gap-2">
            <Plus size={16} /> Post Job
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className={isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}>
              <TableHead>Title</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} className={isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}>
                <TableCell className="font-bold">{job.title}</TableCell>
                <TableCell>{job.department}</TableCell>
                <TableCell>{job.location}</TableCell>
                <TableCell>{job.type}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(job.id)} className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                    <Trash2 size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && (
                <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">No active job listings.</TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
          <DialogHeader>
            <DialogTitle>Post New Opportunity</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className={labelClass}>Job Title</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className={inputClass} placeholder="e.g. Warehouse Operative" />
                </div>
                <div>
                    <Label className={labelClass}>Department</Label>
                    <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className={inputClass} placeholder="e.g. Logistics" />
                </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className={labelClass}>Location</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className={inputClass} />
                </div>
                <div>
                    <Label className={labelClass}>Type</Label>
                    <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                        <SelectTrigger className={inputClass}>
                            <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Full-time">Full-time</SelectItem>
                            <SelectItem value="Part-time">Part-time</SelectItem>
                            <SelectItem value="Contract">Contract</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
              <Label className={labelClass}>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})} 
                className={`${inputClass} h-32`} 
                placeholder="Key responsibilities and requirements..." 
              />
            </div>
            
            <div className="pt-4 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={loading} className="bg-dreibach-teal text-white">
                    {loading ? "Posting..." : "Post Job"}
                </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobManager;
